import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { 
  Clock,
  Users,
  DollarSign,
  TrendingUp,
  ArrowRight,
  Calendar,
  Download,
  Phone,
  Timer,
  Zap,
  Target,
  BarChart3,
  MessageCircle,
  CheckCircle,
  CircleCheckBig,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuizFunnelComponent = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    companyName: '',
    employees: '',
    advisors: '',
    queriesPerEmployee: '',
    resolutionTime: '',
    waitTime: '',
    productivityImpact: '',
    advisorHourlyRate: '',
    monthlyBudget: '',
    email: ''
  });
  const [results, setResults] = useState(null);

  const questions = [
    {
      id: 'contact',
      title: 'Let\'s Get Started',
      subtitle: 'Let\'s personalise your assessment',
      fields: [
        { key: 'firstName', label: 'First Name', type: 'text', placeholder: 'Enter your first name' },
        { key: 'companyName', label: 'Company Name', type: 'text', placeholder: 'Enter your company name' },
        { key: 'email', label: 'Work Email', type: 'email', placeholder: 'your.email@company.com' }
      ]
    },
    {
      id: 'company-size',
      title: 'Your Team Size',
      subtitle: 'This helps us calculate accurate savings',
      fields: [
        { 
          key: 'employees', 
          label: 'How many employees does your company have?', 
          type: 'select',
          options: [
            { value: '50', label: '1-100' },
            { value: '200', label: '101-250' },
            { value: '375', label: '251-500' },
            { value: '750', label: '501-1000' },
            { value: '1500', label: '1001-2000' },
            { value: '2500', label: '2001-3000' },
            { value: '3500', label: '3001-4000' },
            { value: '4500', label: '4001-5000' },
            { value: '6000', label: '5000+' }
          ]
        },
        { 
          key: 'advisors', 
          label: 'How many advisors handle employee questions?', 
          type: 'select',
          options: [
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
            { value: '6', label: '6' },
            { value: '7', label: '7' },
            { value: '8', label: '8' },
            { value: '9', label: '9' },
            { value: '10', label: '10' },
            { value: '11', label: '11' },
            { value: '12', label: '12' },
            { value: '13', label: '13' },
            { value: '14', label: '14' },
            { value: '15', label: '15' }
          ]
        }
      ]
    },
    {
      id: 'current-state',
      title: 'Current Situation',
      subtitle: 'Understanding your current challenges',
      fields: [
        { 
          key: 'queriesPerEmployee', 
          label: 'Approximately how many support queries/questions does your support team receive each month?', 
          type: 'number',
          placeholder: 'Enter number of monthly queries',
          min: 1,
          max: 50000
        },
        { 
          key: 'waitTime', 
          label: 'On average How long do employees wait for answers?', 
          type: 'select',
          options: [
            { value: '0.5', label: '30 minutes or less' },
            { value: '2', label: '1-2 hours' },
            { value: '6', label: '3-6 hours' },
            { value: '12', label: '6-12 hours' },
            { value: '24', label: '1+ days' }
          ]
        }
      ]
    },
    {
      id: 'financial',
      title: 'Final Details',
      subtitle: 'Last step to calculate your ROI',
      fields: [
        { 
          key: 'advisorHourlyRate', 
          label: 'What is the approximate hourly rate of your advisors?', 
          type: 'select',
          options: [
            { value: '25', label: '£20-30 per hour' },
            { value: '35', label: '£30-40 per hour' },
            { value: '45', label: '£40-50 per hour' },
            { value: '55', label: '£50-60 per hour' },
            { value: '65', label: '£60+ per hour' }
          ]
        },
        { 
          key: 'productivityImpact', 
          label: 'How much productivity is lost while employees are waiting for answers?', 
          type: 'select',
          options: [
            { value: '10', label: '10% - Minor slowdown' },
            { value: '25', label: '25% - Some blocked work' },
            { value: '50', label: '50% - Significant delays' },
            { value: '75', label: '75% - Major productivity loss' },
            { value: '100', label: '100% - Complete work stoppage' }
          ]
        }
      ]
    }
  ];

  const calculateResults = () => {
    const employees = parseInt(formData.employees);
    const advisors = parseInt(formData.advisors);
    const totalMonthlyQueries = parseInt(formData.queriesPerEmployee); // Now represents total monthly queries
    const resolutionTimeMinutes = 15; // Average
    const waitTimeHours = parseFloat(formData.waitTime);
    const productivityImpact = parseInt(formData.productivityImpact);
    const advisorHourlyRate = parseInt(formData.advisorHourlyRate);
    
    // Constants
    const automationRate = 0.75;
    const employeeHourlyRate = 22;
    const vioqbaseMonthlyFee = employees > 2000 ? 2500 : employees > 1000 ? 1500 : employees > 500 ? 800 : 400;

    // Calculations
    const automatedQueries = totalMonthlyQueries * automationRate;
    const currentAdvisorHours = (totalMonthlyQueries * resolutionTimeMinutes) / 60;
    const newAdvisorHours = ((totalMonthlyQueries - automatedQueries) * resolutionTimeMinutes) / 60;
    const advisorHoursSaved = currentAdvisorHours - newAdvisorHours;
    const advisorCostSavings = advisorHoursSaved * advisorHourlyRate;

    const productivityHoursSaved = automatedQueries * waitTimeHours * (productivityImpact / 100);
    const productivityValue = productivityHoursSaved * employeeHourlyRate;

    const totalMonthlyValue = advisorCostSavings + productivityValue;
    const netMonthlySavings = totalMonthlyValue - vioqbaseMonthlyFee;
    const annualSavings = netMonthlySavings * 12;
    const totalTimeSaved = advisorHoursSaved + productivityHoursSaved;
    const workingDaysEquivalent = Math.round(totalTimeSaved / 8);

    return {
      advisorHoursSaved: Math.round(advisorHoursSaved),
      productivityHoursSaved: Math.round(productivityHoursSaved),
      totalTimeSaved: Math.round(totalTimeSaved),
      workingDaysEquivalent,
      advisorCostSavings: Math.round(advisorCostSavings),
      productivityValue: Math.round(productivityValue),
      netMonthlySavings: Math.round(netMonthlySavings),
      annualSavings: Math.round(annualSavings),
      automationRate: Math.round(automationRate * 100),
      vioqbaseMonthlyFee,
      totalQueries: totalMonthlyQueries,
      automatedQueries: Math.round(automatedQueries)
    };
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = async () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Submit assessment to backend
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Validate required fields before submission
        const requiredFields = {
          firstName: 'First Name',
          companyName: 'Company Name', 
          email: 'Email',
          employees: 'Number of Employees',
          advisors: 'Number of Advisors',
          queriesPerEmployee: 'Monthly Queries',
          waitTime: 'Wait Time',
          advisorHourlyRate: 'Advisor Hourly Rate',
          productivityImpact: 'Productivity Impact'
        };

        const missingFields = [];
        for (const [key, label] of Object.entries(requiredFields)) {
          if (!formData[key] || formData[key] === '') {
            missingFields.push(label);
          }
        }

        if (missingFields.length > 0) {
          throw new Error(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        }

        // Validate data types and ranges
        const employeeCount = parseInt(formData.employees);
        const advisorCount = parseInt(formData.advisors);
        const queryCount = parseInt(formData.queriesPerEmployee);
        const waitTimeValue = parseFloat(formData.waitTime);
        const hourlyRate = parseInt(formData.advisorHourlyRate);
        const productivityValue = parseInt(formData.productivityImpact);

        if (isNaN(employeeCount) || employeeCount <= 0) {
          throw new Error('Please select a valid number of employees');
        }
        if (isNaN(advisorCount) || advisorCount <= 0) {
          throw new Error('Please select a valid number of advisors');
        }
        if (isNaN(queryCount) || queryCount <= 0) {
          throw new Error('Please select a valid number of monthly queries');
        }
        if (isNaN(waitTimeValue) || waitTimeValue < 0) {
          throw new Error('Please select a valid wait time');
        }
        if (isNaN(hourlyRate) || hourlyRate <= 0) {
          throw new Error('Please select a valid advisor hourly rate');
        }
        if (isNaN(productivityValue) || productivityValue < 0 || productivityValue > 100) {
          throw new Error('Please select a valid productivity impact percentage');
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          throw new Error('Please enter a valid email address');
        }

        const assessmentData = {
          first_name: formData.firstName.trim(),
          company_name: formData.companyName.trim(),
          email: formData.email.trim().toLowerCase(),
          employees: employeeCount,
          advisors: advisorCount,
          queries_per_month: queryCount,
          wait_time: waitTimeValue,
          advisor_hourly_rate: hourlyRate,
          productivity_impact: productivityValue
        };

        // Check if backend URL is available
        if (!BACKEND_URL) {
          throw new Error('Backend service is not configured. Please contact support.');
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout (increased from 30s)

        const response = await fetch(`${BACKEND_URL}/api/assessment/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(assessmentData),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorMessage = 'Failed to submit assessment. Please try again.';
          
          try {
            const errorData = await response.json();
            if (errorData.detail) {
              errorMessage = `Submission failed: ${errorData.detail}`;
            }
          } catch (parseError) {
            // Use default error message if response isn't JSON
          }

          if (response.status === 400) {
            errorMessage = 'Invalid assessment data. Please check your inputs and try again.';
          } else if (response.status === 500) {
            errorMessage = 'Server error. Our team has been notified. Please try again in a few minutes.';
          } else if (response.status === 429) {
            errorMessage = 'Too many requests. Please wait a moment and try again.';
          }

          throw new Error(errorMessage);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.message || 'Assessment submission failed');
        }

        setResults(result.results);
        setShowResults(true);
        
      } catch (error) {
        console.error('Assessment submission error:', error);
        
        let userFriendlyMessage = error.message;
        
        if (error.name === 'AbortError') {
          userFriendlyMessage = 'Request timed out after 60 seconds. This may be due to slow internet connection or server load. Please try again.';
        } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
          userFriendlyMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
        } else if (!navigator.onLine) {
          userFriendlyMessage = 'You appear to be offline. Please check your internet connection and try again.';
        }
        
        setSubmitError(userFriendlyMessage);
        
        // Fallback: calculate results locally if backend fails
        try {
          const calculatedResults = calculateResults();
          setResults(calculatedResults);
          setShowResults(true);
          setSubmitError(prev => prev + ' Results calculated locally, but email may not have been sent.');
        } catch (calcError) {
          console.error('Local calculation also failed:', calcError);
          setSubmitError('Unable to calculate results. Please refresh the page and try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepComplete = () => {
    const currentQuestion = questions[currentStep];
    return currentQuestion.fields.every(field => formData[field.key] && formData[field.key].trim() !== '');
  };

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  const resetQuiz = () => {
    setCurrentStep(0);
    setShowResults(false);
    setIsSubmitting(false);
    setSubmitError(null);
    setFormData({
      firstName: '',
      companyName: '',
      employees: '',
      advisors: '',
      queriesPerEmployee: '',
      resolutionTime: '',
      waitTime: '',
      productivityImpact: '',
      advisorHourlyRate: '',
      monthlyBudget: '',
      email: ''
    });
    setResults(null);
  };

  if (showResults && results) {
    return (
      <div className="space-y-12">
        {/* Results Hero */}
        <Card className="border-0 shadow-2xl bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white overflow-hidden">
          <CardContent className="p-12 text-center relative">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
            <div className="relative z-10">
              <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                🎉 Great News, <span className="text-[#ff7a1f]">{formData.firstName}</span>!
              </h3>
              <h4 className="text-xl lg:text-2xl font-semibold text-blue-100 mb-4">
                VioQbase can save {formData.companyName}:
              </h4>
              
              {!submitError && (
                <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-4 mb-6">
                  <p className="text-green-100 text-lg">
                    ✅ Detailed ROI report sent to <strong>{formData.email}</strong>
                  </p>
                  <p className="text-green-200 text-sm mt-1">
                    Check your inbox for the complete analysis with implementation recommendations
                  </p>
                </div>
              )}
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur border border-white/20 rounded-lg p-6">
                  <DollarSign className="h-10 w-10 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white mb-1">£{results.netMonthlySavings.toLocaleString()}</div>
                  <div className="text-blue-200 text-sm">per month</div>
                </div>
                
                <div className="bg-white/10 backdrop-blur border border-white/20 rounded-lg p-6">
                  <Clock className="h-10 w-10 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white mb-1">{results.totalTimeSaved}</div>
                  <div className="text-blue-200 text-sm">hours per month</div>
                </div>
                
                <div className="bg-white/10 backdrop-blur border border-white/20 rounded-lg p-6">
                  <TrendingUp className="h-10 w-10 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white mb-1">{results.automationRate}%</div>
                  <div className="text-blue-200 text-sm">queries automated</div>
                </div>
              </div>
              
              <div className="bg-[#ff7a1f]/20 rounded-lg p-4 mb-8">
                <p className="text-lg font-semibold">
                  That's equivalent to <span className="text-[#ff7a1f] font-bold">{results.workingDaysEquivalent} full working days</span> returned to your team every month
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How VioQbase Delivers These Savings */}
        <div className="space-y-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-[#112d56] mb-4 flex items-center justify-center space-x-3">
              <BarChart3 className="h-8 w-8 text-[#ff7a1f]" />
              <span>How VioQbase Delivers These Savings</span>
            </h3>
          </div>

          {/* Query Volume Analysis */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#112d56] flex items-center space-x-2">
                <MessageCircle className="h-6 w-6 text-blue-600" />
                <span>Query Volume Analysis</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Based on your input, your support team handles <strong>{results.totalQueries} queries per month</strong>. VioQbase can automatically resolve <strong>{results.automationRate}% of these queries</strong> instantly, which equals <strong>{results.automatedQueries} automated responses</strong> monthly.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Current monthly queries: {results.totalQueries}</li>
                <li>• Queries automated by VioQbase: {results.automatedQueries}</li>
                <li>• Remaining manual queries: {results.totalQueries - results.automatedQueries}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Support Team Efficiency */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-green-100 border-l-4 border-l-green-500">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#112d56] flex items-center space-x-2">
                <Users className="h-6 w-6 text-green-600" />
                <span>Support Team Efficiency</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Your {formData.advisors} support team member{parseInt(formData.advisors) > 1 ? 's' : ''} currently spend significant time on repetitive queries. VioQbase will free up <strong>{results.advisorHoursSaved} hours per month</strong> by automating routine questions, allowing your team to focus on complex issues that truly need human expertise.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Current advisor workload: {Math.round((results.totalQueries * 15) / 60)} hours/month</li>
                <li>• Hours saved through automation: {results.advisorHoursSaved} hours/month</li>
                <li>• Cost savings from freed advisor time: £{results.advisorCostSavings.toLocaleString()}/month</li>
                <li>• Average hours saved per advisor: {Math.round(results.advisorHoursSaved / parseInt(formData.advisors))} hours/month</li>
              </ul>
            </CardContent>
          </Card>

          {/* Employee Productivity Impact */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-orange-50 to-yellow-100 border-l-4 border-l-orange-500">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#112d56] flex items-center space-x-2">
                <Zap className="h-6 w-6 text-orange-600" />
                <span>Employee Productivity Impact</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Your staff currently wait an average of <strong>{formData.waitTime} hours</strong> for answers to their questions. VioQbase provides instant responses, eliminating this wait time and recovering <strong>{results.productivityHoursSaved} productive hours per month</strong> across your {formData.employees} employees.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Current average wait time: {formData.waitTime} hours per query</li>
                <li>• New wait time with VioQbase: Instant (0 hours)</li>
                <li>• Employee productivity recovered: {results.productivityHoursSaved} hours/month</li>
                <li>• Value of recovered productivity: £{results.productivityValue.toLocaleString()}/month</li>
              </ul>
            </CardContent>
          </Card>

          {/* Return on Investment */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-purple-50 to-indigo-100 border-l-4 border-l-purple-500">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#112d56] flex items-center space-x-2">
                <TrendingUp className="h-6 w-6 text-purple-600" />
                <span>Return on Investment</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Your organization will achieve significant savings through VioQbase implementation. The net monthly savings are <strong>£{results.netMonthlySavings.toLocaleString()}</strong>, which compounds to <strong>£{results.annualSavings.toLocaleString()} annually</strong>. This represents exceptional value from day one.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Total monthly value created: £{(results.advisorCostSavings + results.productivityValue).toLocaleString()}</li>
                <li>• Net monthly savings: £{results.netMonthlySavings.toLocaleString()}</li>
                <li>• Annual ROI: £{results.annualSavings.toLocaleString()}</li>
                <li>• ROI Multiple: {((results.netMonthlySavings * 12) / (results.vioqbaseMonthlyFee * 12)).toFixed(1)}x return</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <Card className="border-0 shadow-xl bg-gradient-to-r from-gray-50 to-white">
          <CardContent className="p-8 text-center">
            <h4 className="text-2xl font-bold text-[#112d56] mb-4">
              Ready to unlock these savings for {formData.companyName}?
            </h4>
            <p className="text-gray-600 mb-6">
              Schedule a personalized demo to see how VioQbase can transform your support operations
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={handleDemo}
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Contact Us
              </Button>
              <Button
                onClick={resetQuiz}
                size="lg"
                variant="outline"
                className="border-[#112d56] text-[#112d56] hover:bg-[#112d56] hover:text-white px-8 py-3 rounded-lg"
              >
                Take Assessment Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = questions[currentStep];

  return (
    <div className="relative">
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-[#ff7a1f]/20 rounded-full blur-2xl opacity-60"></div>
      <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#112d56]/20 rounded-full blur-2xl opacity-60"></div>
      
      <div className="max-w-4xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <Timer className="h-4 w-4 text-[#ff7a1f]" />
              <span className="text-sm font-medium text-gray-700">Progress</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">{currentStep + 1} of {questions.length}</span>
              <div className="px-2 py-1 bg-[#ff7a1f]/10 rounded-full">
                <span className="text-xs font-bold text-[#ff7a1f]">{Math.round(((currentStep + 1) / questions.length) * 100)}%</span>
              </div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
            <div 
              className="bg-gradient-to-r from-[#ff7a1f] to-[#e6681b] h-3 rounded-full transition-all duration-500 shadow-sm relative overflow-hidden"
              style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Quiz Card */}
        <Card className="border-0 shadow-2xl bg-white relative overflow-hidden transform hover:scale-[1.01] transition-all duration-300">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#ff7a1f] via-[#e6681b] to-[#ff7a1f]"></div>
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-[#ff7a1f]/10 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#112d56]/5 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute top-4 left-4 w-16 h-16 bg-[#ff7a1f]/5 rounded-full blur-xl"></div>
          
          <CardHeader className="text-center pb-8 pt-12 relative z-10 bg-gradient-to-b from-gray-50/50 to-transparent">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#ff7a1f] to-[#e6681b] rounded-2xl mb-6 shadow-lg">
              <MessageCircle className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-3xl lg:text-4xl font-bold text-[#112d56] mb-4">
              {currentQuestion.title}
            </CardTitle>
            <CardDescription className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              {currentQuestion.subtitle}
            </CardDescription>
            <div className="mt-6 w-24 h-1 bg-gradient-to-r from-[#ff7a1f] to-[#e6681b] rounded-full mx-auto"></div>
          </CardHeader>
          
          <CardContent className="space-y-8 px-8 lg:px-12 pb-12 relative z-10">
            <div className="grid md:grid-cols-1 gap-8">
              {currentQuestion.fields.map((field, index) => (
                <div key={field.key} className="space-y-4 group">
                  <label className="block text-lg font-bold text-[#112d56] flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#ff7a1f] rounded-full"></div>
                    <span>{field.label}</span>
                    {field.type === 'email' && <span className="text-red-500 text-xl">*</span>}
                  </label>
                  
                  <div className="space-y-2">
                    {field.type === 'select' ? (
                      <select
                        value={formData[field.key]}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="w-full p-6 text-lg border-2 rounded-2xl focus:outline-none focus:ring-4 text-gray-700 bg-white transition-all duration-300 hover:shadow-lg border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 hover:border-[#ff7a1f]/50"
                      >
                        <option value="">Select an option...</option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        value={formData[field.key]}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        min={field.min}
                        max={field.max}
                        className="w-full p-6 text-lg border-2 rounded-2xl focus:outline-none focus:ring-4 text-gray-700 bg-white transition-all duration-300 hover:shadow-lg border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 hover:border-[#ff7a1f]/50"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-between pt-8 border-t-2 border-gray-100">
              {currentStep > 0 && (
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-700 px-8 py-4 text-lg font-bold rounded-2xl transition-all duration-300"
                >
                  Back
                </Button>
              )}
              
              <Button
                onClick={handleNext}
                disabled={!isStepComplete() || isSubmitting}
                className={`ml-auto px-12 py-4 text-lg font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 ${
                  isStepComplete() && !isSubmitting
                    ? 'bg-gradient-to-r from-[#ff7a1f] to-[#e6681b] hover:from-[#e6681b] hover:to-[#ff7a1f] text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Calculating & Sending Results...
                  </>
                ) : (
                  <>
                    {currentStep === questions.length - 1 ? 'Calculate My Savings' : 'Next Step'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
              
              {submitError && (
                <div className="text-red-600 text-sm mt-4 text-center">
                  {submitError}
                  <br />
                  <span className="text-gray-600">Results calculated locally, but email may not have been sent.</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuizFunnelComponent;